/** In-app pieces for a self sign-up whose account isn't open yet: a bar across the app and the locked Deposit page. */
import type { Me } from '@platform/shared';
import { Icon } from '../../components/ui';
import { Link } from '../../lib/router';
import { useMe } from '../../stores/session';
import { Card, Chip, H1, PillLink } from '../account/kit';

type Lock = { tone: 'warn' | 'down' | 'accent'; chip: string; bar: string; title: string; text: string; cta: string; to: string };

/** What the client still has to do before deposits and trading open, or null when nothing. */
export function accountLock(me: Me | null): Lock | null {
  if (!me) return null;
  switch (me.onboardingStep) {
    case 'details':
    case 'identity':
      return {
        tone: 'accent',
        chip: 'Verification needed',
        bar: 'Finish verifying your identity to open your account.',
        title: 'Finish verifying your identity',
        text: 'Deposits and trading open once your identity is verified. It takes about 2 minutes, then our team reviews it within 24 hours.',
        cta: 'Continue verification',
        to: '/onboarding',
      };
    case 'review':
      return me.kycStatus === 'rejected'
        ? {
            tone: 'down',
            chip: 'Action needed',
            bar: 'We need a new document to verify your identity.',
            title: 'We need a new document',
            text: "We couldn't verify your identity with the documents you sent. Replace the one we asked for and we'll review it again.",
            cta: 'Upload new document',
            to: '/onboarding/review',
          }
        : {
            tone: 'warn',
            chip: 'Under review',
            bar: 'Your account is under review. Deposits and trading open once it is approved, within 24 hours.',
            title: 'Deposits open once your account is approved',
            text: "Our compliance team is verifying your identity. This can take up to 24 hours, and we'll email you as soon as it's done.",
            cta: 'View review status',
            to: '/onboarding/review',
          };
    case 'questions':
      return {
        tone: 'accent',
        chip: 'One last step',
        bar: "You're verified. Answer a few questions to make your first deposit.",
        title: 'One last step before your first deposit',
        text: 'Answer 7 quick questions about your finances and trading experience. Regulation requires us to check that CFDs suit you.',
        cta: 'Answer the questions',
        to: '/onboarding/questions',
      };
    default:
      return null;
  }
}

const TONE = { warn: 'bg-warn-bg text-warn', down: 'bg-down-bg text-down', accent: 'bg-accent-bg text-accent-fg' };

/** Thin bar under the top navigation while the account isn't open. */
export function AccountStatusBar() {
  const lock = accountLock(useMe());
  if (!lock) return null;
  return (
    <div className={`flex items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-[13px] max-sm:flex-col ${TONE[lock.tone]}`}>
      <span className="flex items-center gap-2 font-medium">
        <Icon name="lock" size={13} />
        {lock.bar}
      </span>
      <Link to={lock.to} className="font-semibold text-fg underline underline-offset-4">
        {lock.cta}
      </Link>
    </div>
  );
}

/** Full Deposit page while the account isn't open, in place of the deposit form. */
export function DepositLocked() {
  const me = useMe();
  const lock = accountLock(me);
  if (!lock) return null;
  return (
    <Card className="flex max-w-[760px] flex-col items-start gap-4">
      <Chip tone={lock.tone === 'accent' ? 'accent' : lock.tone}>{lock.chip}</Chip>
      <H1>{lock.title}</H1>
      <p className="m-0 text-[16px] text-muted md:text-[17px]">{lock.text}</p>
      <div className="flex flex-wrap gap-3">
        <PillLink to={lock.to}>{lock.cta}</PillLink>
        <PillLink to="/account/support" kind="secondary">
          Contact support
        </PillLink>
      </div>
    </Card>
  );
}
